import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

/**
 * SuccessOrder / SuccessPopup
 *
 * Used in CheckoutPageNew as:
 *   import SuccessOrder from "../../components/SuccessOrder";
 *   import SuccessPopup from "../../components/SuccessOrder";
 *   <SuccessPopup open={showSuccess} onClose={() => setShowSuccess(false)} />
 *
 * Behaviour:
 *  - Renders as a glass modal overlay (not a full page) on top of checkout
 *  - Counts down 4 → 3 → 2 → 1, then navigates to /shop
 *  - "Go to Shop" button skips the countdown immediately
 *  - Matches the bg-white/40 glass design system used across the project
 */
export default function SuccessOrder({ open = false, onClose }) {
  const navigate   = useNavigate();
  const [count, setCount] = useState(4);

  /* Reset counter every time the popup opens */
  useEffect(() => {
    if (!open) {
      setCount(4);
      return;
    }

    const interval = setInterval(() => {
      setCount((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          onClose?.();
          navigate("/shop");
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [open]);

  if (!open) return null;

  const handleGoNow = () => {
    onClose?.();
    navigate("/shop");
  };

  return (
    /* ── Backdrop ─────────────────────────────────────────────── */
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-4">

      {/* ── Glass card ─────────────────────────────────────────── */}
      <div
        className="relative w-full max-w-md bg-white/40 backdrop-blur-xl border border-white/25 rounded-2xl shadow-lg p-8 text-center"
        style={{ WebkitBackdropFilter: "blur(14px) saturate(140%)" }}
      >

        {/* ── Animated checkmark circle ─── */}
        <div className="flex justify-center mb-6">
          <div className="w-20 h-20 rounded-full bg-green-100/80 border border-green-200 flex items-center justify-center animate-[scale-in_0.4s_ease-out]">
            <svg
              className="w-10 h-10 text-green-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M5 13l4 4L19 7"
                className="[stroke-dasharray:30] [stroke-dashoffset:30] animate-[draw_0.5s_0.2s_ease-out_forwards]"
                style={{
                  strokeDasharray: 30,
                  strokeDashoffset: 30,
                  animation: "draw 0.5s 0.2s ease-out forwards",
                }}
              />
            </svg>
          </div>
        </div>

        {/* ── Heading ─── */}
        <h2 className="text-2xl font-bold text-gray-800 mb-2">
          Order Placed! 🎉
        </h2>

        {/* ── Sub-text ─── */}
        <p className="text-gray-600 text-sm leading-relaxed mb-1">
          Your order has been placed successfully and is now being processed.
        </p>
        <p className="text-gray-500 text-sm mb-8">
          You'll receive a confirmation shortly.
        </p>

        {/* ── Countdown ring + label ─── */}
        <div className="flex flex-col items-center gap-2 mb-8">
          {/* SVG countdown ring */}
          <div className="relative w-14 h-14">
            <svg className="w-14 h-14 -rotate-90" viewBox="0 0 56 56">
              {/* Track */}
              <circle
                cx="28" cy="28" r="24"
                fill="none"
                stroke="#e5e7eb"
                strokeWidth="4"
              />
              {/* Progress — shrinks as count decreases */}
              <circle
                cx="28" cy="28" r="24"
                fill="none"
                stroke="#16a34a"
                strokeWidth="4"
                strokeLinecap="round"
                strokeDasharray={`${2 * Math.PI * 24}`}
                strokeDashoffset={`${2 * Math.PI * 24 * (1 - count / 4)}`}
                style={{ transition: "stroke-dashoffset 0.9s linear" }}
              />
            </svg>
            {/* Number in the middle */}
            <span className="absolute inset-0 flex items-center justify-center text-lg font-bold text-green-700">
              {count}
            </span>
          </div>
          <p className="text-xs text-gray-500">
            Redirecting to shop in {count}s…
          </p>
        </div>

        {/* ── Action buttons ─── */}
        <div className="flex flex-col gap-3">
          <button
            onClick={handleGoNow}
            className="w-full px-6 py-3 rounded-xl bg-blue-600 hover:bg-blue-700 active:scale-95 transition-all text-white font-semibold text-sm shadow"
          >
            Go to Shop Now →
          </button>
          <a
            href="/"
            className="w-full px-6 py-2.5 rounded-xl bg-white/60 hover:bg-white/80 transition text-gray-700 font-medium text-sm text-center border border-white/40"
          >
            Back to Home
          </a>
        </div>

      </div>

      {/* Keyframe for checkmark draw */}
      <style>{`
        @keyframes draw {
          to { stroke-dashoffset: 0; }
        }
        @keyframes scale-in {
          from { transform: scale(0.5); opacity: 0; }
          to   { transform: scale(1);   opacity: 1; }
        }
      `}</style>
    </div>
  );
}